import _ from 'lodash'
import { AxiosRequestHeaders, AxiosResponse, Method } from 'axios'

export interface IAuthenticatedRequestHandlerOptions {
    method: Method
    url: string
    headers?: AxiosRequestHeaders
    body?: any
}

export interface IAuthenticatedRequestHandlerResponse<T = any> {
    ok: boolean
    status: number
    statusText: string
    url: string
    headers: AxiosRequestHeaders
    body: T
    launchDate?: number
}

const SUPPORTED_METHODS = Object.freeze([
    'GET',
    'POST',
    'PUT',
    'PATCH',
    'DELETE',
])

export class AuthenticatedRequestHandler {
    /**
     * Returns a Promise which resolves to an http response (regardless of the HTTP response code)
     * and only rejects when the HTTP request couldn't be sent (i.e. networking error / bad arguments).
     * This API is only meant to be used when the authentication mechanism is IhmLogin or Midway and
     * will reject with an {@see UnsupportedEnvironmentException} if we're not either of those contexts.
     *
     * @param {object} options - Options for the request.
     *  {
     *      url: {String}, // required,
     *      method: {Method}, // Axios Method type
     *      headers: {Object}, // optional headers. The Authorization header is explicitly not supported
     *                               and will result in an {@see InvalidArgumentsException},
     *      body: {any}, // optional body to include with the request.
     *      queryParams: {Object} // optional query param to include with request.
     *  }
     *  The options object is modeled after the {
     *  @link  https://developer.mozilla.org/en-US/docs/Web/API/Request} object.
     *  @return {Promise} - Resolves with an HTTP Response and rejects with a {@link SeawardException}.
     *  HTTP Response:
     *      {
     *          ok: {Boolean} - Shorthand to check if the response had a 2xx status code.
     *          status: {Number} - The HTTP status code.
     *          statusText: {String} - The status text (i.e. "OK")
     *          url: {String} - The URL of the request.
     *          headers: {Object} - Response headers
     *          body: {any} - Te body of the response
     *      }
     */
    public static raw (options: IAuthenticatedRequestHandlerOptions): Promise<IAuthenticatedRequestHandlerResponse> {
        // Mock API responses until the actual implementation is in place.
        console.log('mocking API call to:', options.url)

        let mockBody: any = { success: true, mockData: true }

        if (options.url.includes('GetTreatmentAssignment')) {
            mockBody = {
                treatmentAssignment: {
                    treatmentName: 'T1',
                },
            }
        } else if (options.url.includes('GetMenu')) {
            mockBody = {
                menu: {
                    zones: [
                        {
                            zoneName: 'Development',
                            displayNameStringId: '',
                            subzones: [
                                {
                                    subzoneName: 'Tools',
                                    displayNameStringId: '',
                                    tools: [
                                        {
                                            displayName: 'Migrated Tool',
                                            displayNameStringId: '',
                                            metadata: { cti: '', package: '' },
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
                toolRegistrations: {
                    'Migrated Tool': {
                        path: 'https://localhost.aka.corp.amazon.com:8080',
                        metadata: {},
                    },
                },
            }
        }

        return Promise.resolve({
            ok: true,
            status: 200,
            statusText: 'OK',
            url: options.url,
            headers: {},
            body: mockBody,
        })
    }
}
